import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { C2Button, C2Pill, C2GoTag, C2Section, C2Shape } from '../c2';
import type { DecorShape } from '../c2/Section';
import HeroTabCascade from './HeroTabCascade';
import InteractiveBrowserDemo from '../demo/InteractiveBrowserDemo';

const whatDecor: DecorShape[] = [
  { shape: 'circle', size: 240, color: 'mint', style: { position: 'absolute', top: 80, left: -100, opacity: 0.45 } },
  { shape: 'pill', size: 240, color: 'peach', style: { position: 'absolute', top: 220, right: -70, opacity: 0.45, transform: 'rotate(8deg)' } },
  { shape: 'square', size: 140, color: 'lilac', style: { position: 'absolute', bottom: 80, left: '12%', opacity: 0.45, transform: 'rotate(-10deg)' } },
];

const usedAtDecor: DecorShape[] = [
  { shape: 'circle', size: 130, color: 'lemon', style: { position: 'absolute', top: -40, left: '8%', opacity: 0.4 } },
  { shape: 'circle', size: 110, color: 'sky', style: { position: 'absolute', bottom: -30, right: '12%', opacity: 0.45 } },
];

const howDecor: DecorShape[] = [
  { shape: 'circle', size: 240, color: 'sky', style: { position: 'absolute', top: 80, left: -110, opacity: 0.45 } },
  { shape: 'pill', size: 220, color: 'peach', style: { position: 'absolute', top: 60, right: -70, opacity: 0.45, transform: 'rotate(-8deg)' } },
  { shape: 'square', size: 140, color: 'lemon', style: { position: 'absolute', bottom: 60, right: '15%', opacity: 0.45, transform: 'rotate(10deg)' } },
  { shape: 'circle', size: 180, color: 'mint', style: { position: 'absolute', bottom: 40, left: '18%', opacity: 0.45 } },
];

const featuresDecor: DecorShape[] = [
  { shape: 'circle', size: 240, color: 'lilac', style: { position: 'absolute', top: 60, left: -100, opacity: 0.45 } },
  { shape: 'pill', size: 240, color: 'mint', style: { position: 'absolute', top: 220, right: -80, opacity: 0.45, transform: 'rotate(8deg)' } },
  { shape: 'square', size: 130, color: 'peach', style: { position: 'absolute', bottom: 80, left: '10%', opacity: 0.45, transform: 'rotate(-10deg)' } },
  { shape: 'circle', size: 200, color: 'lemon', style: { position: 'absolute', bottom: 40, right: -60, opacity: 0.45 } },
];

const faqTeaserDecor: DecorShape[] = [
  { shape: 'circle', size: 240, color: 'peach', style: { position: 'absolute', top: 80, left: -110, opacity: 0.45 } },
  { shape: 'pill', size: 220, color: 'sky', style: { position: 'absolute', top: 240, right: -70, opacity: 0.45, transform: 'rotate(8deg)' } },
  { shape: 'square', size: 130, color: 'mint', style: { position: 'absolute', bottom: 60, left: '15%', opacity: 0.45, transform: 'rotate(-10deg)' } },
];

// ============================================================
// HERO (two-column cascade variant — per design_handoff_hero)
// ============================================================

export function HeroSection() {
  return (
    <section
      className="relative overflow-hidden"
      style={{ padding: '60px clamp(32px, 6vw, 96px) 20px' }}
    >
      <C2Shape shape="circle" size={320} color="peach" style={{ position: 'absolute', top: -100, left: -120, opacity: 0.5 }} />
      <C2Shape shape="circle" size={200} color="sky" style={{ position: 'absolute', top: 40, right: -60, opacity: 0.5 }} />
      <C2Shape shape="pill" size={240} color="lilac" style={{ position: 'absolute', bottom: -40, left: '18%', opacity: 0.5, transform: 'rotate(-10deg)' }} />
      <div className="max-w-[1200px] mx-auto relative">
        <div
          className="grid items-start mb-10"
          style={{ gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 0.85fr)', gap: 56 }}
        >
          <div>
            <h1
              className="font-display font-extrabold text-ink m-0 mb-7"
              style={{ fontSize: 'clamp(48px, 6vw, 104px)', lineHeight: 0.92, letterSpacing: '-0.045em' }}
            >
              short links,
              <br />
              for <span style={{ color: 'var(--accent-solid)' }}>teams</span>
            </h1>
            <p className="text-[22px] text-ink-soft leading-[1.55] m-0 max-w-[520px]">
              Stop hunting for internal resources. Create go/ links that everyone can remember.
            </p>
          </div>
          <div style={{ marginTop: -52, marginLeft: -100 }}>
            <HeroTabCascade />
            <div
              className="flex flex-col items-start"
              style={{ marginTop: 12, marginLeft: 60, gap: 14 }}
            >
              <HeroCTAs />
              <span className="text-[13px] text-ink-muted">Free forever · No credit card · 60-second setup</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroCTAs() {
  return (
    <div className="flex gap-3.5 items-center flex-wrap">
      <Link to="/gatekeeper">
        <C2Button variant="accent" size="xl">
          Sign Up →
        </C2Button>
      </Link>
      <Link to="/#how">
        <C2Button variant="ghost" size="xl">
          What are go/ links?
        </C2Button>
      </Link>
    </div>
  );
}

// ============================================================
// WHAT ARE GO/ LINKS
// ============================================================

export function WhatAreGoLinksSection() {
  return (
    <C2Section pad={120} bg="var(--color-bg-alt)" decor={whatDecor}>
      <div id="how" style={{ scrollMarginTop: 96 }} className="text-center mb-[60px] max-w-[760px] mx-auto">
        <h2
          className="font-display font-extrabold text-ink m-0 mb-5"
          style={{ fontSize: 'clamp(44px, 6vw, 84px)', lineHeight: 1, letterSpacing: '-0.035em' }}
        >
          What are go/ links?
        </h2>
        <p className="text-lg text-ink-soft leading-[1.6] m-0">
          Go/ links are easy to remember shortcuts that you and your teams can create to navigate internal resources easily. Go/ links originated at Google and are now used at most major tech companies. Tidy Go Links brings this productivity tool to every organization.
        </p>
      </div>
      <InteractiveBrowserDemo />
    </C2Section>
  );
}

// ============================================================
// USED AT
// ============================================================

export function UsedAtSection() {
  const companies = ['Google', 'Meta', 'Stripe', 'Figma', 'Airbnb', 'Netflix', 'Pinterest', 'Uber'];
  return (
    <C2Section pad={56} bg="var(--color-surface)" className="border-t border-b border-hairline" decor={usedAtDecor}>
      <p className="text-center text-sm text-ink-muted m-0 mb-7">
        Used by hundreds of thousands of employees at
      </p>
      <div className="flex flex-wrap justify-center text-[22px] font-semibold text-ink-soft" style={{ gap: '20px 48px' }}>
        {companies.map(n => (
          <span key={n}>{n}</span>
        ))}
      </div>
    </C2Section>
  );
}

// ============================================================
// HOW IT WORKS
// ============================================================

const howSteps: Array<{ n: number; t: string; d: ReactNode; bg: 'peach' | 'mint' | 'sky' }> = [
  {
    n: 1,
    t: 'Install',
    d: (
      <>
        Add the{' '}
        <a
          href="https://chromewebstore.google.com/detail/tidy-go-links/nijbmhklldgocejmopojcapjiacnepja"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:no-underline"
          style={{ color: 'var(--color-ink)' }}
        >
          Tidy Go Links extension
        </a>{' '}
        to Chrome or any Chromium based browser.
      </>
    ),
    bg: 'peach',
  },
  { n: 2, t: 'Create', d: 'Pick a memorable go/link name (like go/vacation or go/okrs).', bg: 'mint' },
  { n: 3, t: 'Go', d: 'Type go/anything in your address bar. Your browser resolves it and takes you there.', bg: 'sky' },
];

const pastelBgVars = {
  peach: 'var(--color-peach)',
  peachDeep: 'var(--color-peach-deep)',
  mint: 'var(--color-mint)',
  mintDeep: 'var(--color-mint-deep)',
  sky: 'var(--color-sky)',
  skyDeep: 'var(--color-sky-deep)',
} as const;

export function HowItWorksSection() {
  return (
    <C2Section pad={120} decor={howDecor}>
      <div className="text-center mb-[72px]">
        <C2Pill color="lemon" className="mb-5">How it works</C2Pill>
        <h2
          className="font-display font-extrabold text-ink m-0"
          style={{ fontSize: 'clamp(44px, 6vw, 84px)', lineHeight: 1, letterSpacing: '-0.035em' }}
        >
          Three steps. Done.
        </h2>
      </div>
      <div className="grid grid-cols-3 gap-6">
        {howSteps.map(s => (
          <div
            key={s.n}
            className="rounded-xl relative overflow-hidden"
            style={{ background: pastelBgVars[s.bg], padding: '48px 36px' }}
          >
            <div
              className="absolute top-[-40px] right-[-40px] w-40 h-40 rounded-full"
              style={{ background: 'rgba(255,255,255,0.4)' }}
            />
            <div className="relative">
              <div
                className="font-display font-extrabold mb-6"
                style={{
                  fontSize: 120,
                  lineHeight: 1,
                  letterSpacing: '-0.05em',
                  color: pastelBgVars[`${s.bg}Deep` as keyof typeof pastelBgVars],
                }}
              >
                {s.n}
              </div>
              <h3
                className="font-display font-bold text-ink m-0 mb-3"
                style={{ fontSize: 34, letterSpacing: '-0.02em' }}
              >
                {s.t}
              </h3>
              <p className="text-base text-ink-soft leading-[1.6] m-0">{s.d}</p>
            </div>
          </div>
        ))}
      </div>
    </C2Section>
  );
}

// ============================================================
// DASHBOARD PREVIEW
// ============================================================

export function DashboardPreviewSection() {
  const links: Array<[string, string, number, string]> = [
    ['payroll', 'workday.com/payroll', 342, '↑ 12%'],
    ['okrs', 'docs.google.com/spreadsheets/d/abc...', 187, '↑ 4%'],
    ['wiki', 'notion.so/acme/wiki', 156, '↓ 3%'],
    ['oncall', 'pagerduty.com/schedules/PXYZ42', 94, '↑ 18%'],
    ['sre-dash', 'grafana.acme.com/d/kL92f', 71, '↑ 22%'],
  ];

  const sidebarItems: Array<[string, string, boolean]> = [
    ['🔗', 'My links', true],
    ['📊', 'Stats', false],
    ['🏢', 'Organization', false],
    ['👥', 'Members', false],
    ['⚙️', 'Settings', false],
  ];

  return (
    <C2Section pad={100}>
      <div className="text-center mb-14">
        <C2Pill color="lilac" className="mb-5">Your dashboard</C2Pill>
        <h2
          className="font-display font-extrabold text-ink m-0 mb-5"
          style={{ fontSize: 'clamp(40px, 5.5vw, 72px)', lineHeight: 1.02, letterSpacing: '-0.03em' }}
        >
          A dashboard that stays out of your way.
        </h2>
        <p className="text-[19px] text-ink-soft max-w-[560px] mx-auto leading-[1.6] m-0">
          Create links, see what your team uses most, and manage members. That's it.
        </p>
      </div>

      <div
        className="bg-surface border border-hairline rounded-xl overflow-hidden"
        style={{ boxShadow: 'var(--shadow-lg)' }}
      >
        <div className="px-5 py-3.5 border-b border-hairline flex items-center gap-2.5 bg-bg-alt">
          <div className="flex gap-1.5">
            <span className="w-3 h-3 rounded-full" style={{ background: 'var(--color-peach)' }} />
            <span className="w-3 h-3 rounded-full" style={{ background: 'var(--color-lemon)' }} />
            <span className="w-3 h-3 rounded-full" style={{ background: 'var(--color-mint)' }} />
          </div>
          <div className="flex-1 text-[13px] text-center text-ink-muted font-mono">tidygolinks.com/dashboard</div>
        </div>
        <div className="grid grid-cols-[220px_1fr]">
          <div className="p-5 border-r border-hairline text-[15px] min-h-[520px]">
            {sidebarItems.map(([icon, label, active]) => (
              <div
                key={label}
                className="flex items-center gap-2.5 px-3.5 py-[11px] rounded-md mb-1"
                style={{
                  background: active ? 'var(--accent-soft)' : 'transparent',
                  color: active ? 'var(--accent-deep)' : 'var(--color-ink-soft)',
                  fontWeight: active ? 600 : 500,
                }}
              >
                <span className="text-base">{icon}</span>
                {label}
              </div>
            ))}
          </div>
          <div className="p-8">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3
                  className="font-display font-bold text-ink m-0"
                  style={{ fontSize: 30, letterSpacing: '-0.015em' }}
                >
                  Welcome back, Rachel
                </h3>
                <p className="text-sm text-ink-muted mt-1.5 mb-0">47 links · 1,284 clicks this week</p>
              </div>
              <C2Button variant="accent" size="md">+ New go/ link</C2Button>
            </div>
            <div className="border border-hairline rounded-lg overflow-hidden">
              {links.map(([s, d, c, delta], i) => (
                <div
                  key={s}
                  className="grid grid-cols-[180px_1fr_100px_80px] items-center text-sm"
                  style={{
                    padding: '14px 18px',
                    borderTop: i ? '1px solid var(--color-hairline)' : 'none',
                  }}
                >
                  <C2GoTag slug={s} size="md" />
                  <span className="text-ink-muted text-[13px] font-mono">{d}</span>
                  <span className="text-right text-[13px] text-ink-soft font-medium">{c} clicks</span>
                  <span
                    className="text-right text-xs"
                    style={{ color: delta.startsWith('↑') ? 'var(--color-mint-deep)' : 'var(--color-ink-muted)' }}
                  >
                    {delta}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </C2Section>
  );
}

// ============================================================
// FEATURE GRID
// ============================================================

const features = [
  { icon: '🌐', t: 'Works everywhere', d: 'Chrome, Edge, Brave, Arc — any Chromium browser. Firefox support coming soon.' },
  { icon: '👥', t: 'Shared with your team', d: 'Auto-grouped by your email domain. Everyone at your company sees the same links.' },
  { icon: '🔀', t: 'Alternate names', d: 'Create go/hr, go/people, and go/human-resources all pointing to the same place.' },
  { icon: '📊', t: 'Click analytics', d: 'See which links your team actually uses. No tracking beyond the dashboard.' },
  { icon: '🔒', t: 'Private by default', d: 'Your links are visible only to your workspace. No public directory, no data resale.' },
  { icon: '🧑‍💻', t: 'Open source', d: 'Self-host if you want to. Fork if you feel like it.' },
];

export function FeatureGridSection() {
  return (
    <C2Section pad={100} bg="var(--color-surface)" className="border-t border-b border-hairline" decor={featuresDecor}>
      <div className="text-center mb-16">
        <C2Pill color="mint" className="mb-5">Everything included</C2Pill>
        <h2
          className="font-display font-extrabold text-ink m-0 mb-4"
          style={{ fontSize: 'clamp(40px, 5.5vw, 72px)', lineHeight: 1, letterSpacing: '-0.03em' }}
        >
          No paywalls. No feature gates.
        </h2>
        <p className="text-lg text-ink-soft max-w-[560px] mx-auto leading-[1.6] m-0">
          Every feature we build ships to everyone for free, full stop.
        </p>
      </div>
      <div className="grid grid-cols-3 gap-5">
        {features.map((f, i) => (
          <div
            key={i}
            className="p-8 rounded-xl bg-bg border border-hairline"
          >
            <div className="text-[32px] mb-4">{f.icon}</div>
            <h3
              className="font-display font-bold text-ink m-0 mb-2.5"
              style={{ fontSize: 22, letterSpacing: '-0.01em' }}
            >
              {f.t}
            </h3>
            <p className="text-[15px] text-ink-soft leading-[1.6] m-0">{f.d}</p>
          </div>
        ))}
      </div>
    </C2Section>
  );
}

// ============================================================
// FAQ TEASER
// ============================================================

const faqs = [
  { q: 'Is Tidy Go Links really free?', a: "Yes. 100% free and open source. No paid tiers, no feature gates, no usage limits. We built this because we wanted go/ links at every company we work at." },
  { q: 'How do I set up go/ links for my team?', a: "Sign up with your work email. Your workspace is created automatically for your domain. Anyone at your company who signs up joins the same workspace. Install the extension, start creating links." },
  { q: 'Do I need the browser extension?', a: "In a word: yes. Tidy Go Links only works like you expect it to if you have the extension. Alternatively, you can grab the git repo and deploy & manage Tidy Go Links in your company and avoid anyone having to use an extension." },
  { q: 'Who can see my go/ links?', a: "Everyone in your workspace. If you're in an organization workspace, everyone in your organization sees all the links that users in that organization have created (this is the magic of go/ links: one person points go/vacation at the vacation request site, and everyone benefits). Personal workspaces are private to you." },
];

export function FAQTeaserSection() {
  const [open, setOpen] = useState(0);
  return (
    <C2Section pad={100} bg="var(--color-surface)" className="border-t border-b border-hairline" decor={faqTeaserDecor}>
      <div className="grid grid-cols-[1fr_1.5fr] gap-20 items-start">
        <div>
          <C2Pill color="peach" className="mb-5">Common questions</C2Pill>
          <h2
            className="font-display font-extrabold text-ink m-0 mb-4"
            style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1.02, letterSpacing: '-0.03em' }}
          >
            Questions you might have.
          </h2>
          <p className="text-[17px] text-ink-soft leading-[1.6] m-0 mb-6">
            The short version: it's free, it works, we won't sell your data.
          </p>
          <Link to="/faq">
            <C2Button variant="outline">Read full FAQ →</C2Button>
          </Link>
        </div>
        <div>
          {faqs.map((f, i) => (
            <div
              key={i}
              className="border-t border-hairline"
              style={{ borderBottom: i === faqs.length - 1 ? '1px solid var(--color-hairline)' : 'none' }}
            >
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full bg-transparent border-0 cursor-pointer text-left flex justify-between items-center font-display font-semibold text-ink"
                style={{ padding: '22px 0', fontSize: 22, letterSpacing: '-0.01em' }}
              >
                {f.q}
                <span
                  className="text-2xl transition-transform duration-200"
                  style={{
                    color: open === i ? 'var(--accent-solid)' : 'var(--color-ink-muted)',
                    transform: open === i ? 'rotate(45deg)' : 'rotate(0)',
                  }}
                >
                  +
                </span>
              </button>
              <div
                className="overflow-hidden transition-[max-height] duration-300 ease-out"
                style={{ maxHeight: open === i ? 240 : 0 }}
              >
                <p className="text-base text-ink-soft leading-[1.65] m-0 mb-[22px] max-w-[560px]">{f.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </C2Section>
  );
}

// ============================================================
// FINAL CTA
// ============================================================

export function FinalCTASection() {
  return (
    <C2Section pad={120} className="text-center relative overflow-hidden">
      <C2Shape shape="circle" size={420} color="peach" className="absolute opacity-50" style={{ top: -100, left: '10%' }} />
      <C2Shape shape="circle" size={320} color="sky" className="absolute opacity-50" style={{ bottom: -80, right: '15%' }} />
      <C2Shape shape="circle" size={160} color="lemon" className="absolute opacity-70" style={{ top: 80, right: '25%' }} />
      <div className="relative max-w-[800px] mx-auto">
        <div
          className="w-20 h-20 rounded-full mx-auto mb-8 flex items-center justify-center text-[32px]"
          style={{ background: 'var(--accent-soft)', color: 'var(--accent-deep)' }}
        >
          ✦
        </div>
        <h2
          className="font-display font-extrabold text-ink m-0 mb-6"
          style={{ fontSize: 'clamp(52px, 7.5vw, 112px)', lineHeight: 0.98, letterSpacing: '-0.04em' }}
        >
          Let's go/<span style={{ color: 'var(--accent-solid)' }}>.</span>
        </h2>
        <p className="text-xl text-ink-soft max-w-[500px] mx-auto leading-[1.55] mb-10 m-0">
          Free, open source, ready in 60 seconds. No credit card required.
        </p>
        <div className="flex gap-3.5 justify-center flex-wrap">
          <Link to="/gatekeeper">
            <C2Button variant="accent" size="xl">
              Create your first go/ link →
            </C2Button>
          </Link>
          <Link to="/compare">
            <C2Button variant="outline" size="xl">
              Compare to alternatives
            </C2Button>
          </Link>
        </div>
      </div>
    </C2Section>
  );
}
